/**
 * swr — generic stale-while-revalidate helper for AWENI.
 *
 * Pattern:
 *   - If cached entry is "fresh" (age < freshTtlMs): return cached, no network.
 *   - If cached entry is "stale" (freshTtlMs ≤ age < staleTtlMs): return cached
 *     immediately AND kick off background revalidation; when network resolves,
 *     the cache is updated and `onUpdate` is invoked so the widget can re-render.
 *   - If beyond staleTtlMs OR no cache: do the fetch synchronously, cache the
 *     result, and return it. If fetch fails and we have stale data, return that
 *     instead (offline-tolerant).
 *
 * Storage: localStorage (sync API, sufficient for small JSON payloads —
 * weather, news headlines, geocode results all fit in ~5-10MB origin budget).
 *
 * Use: every location-dependent service routes through `swr()` so widgets
 * always render fast (cached) and the UI never blocks on a network call.
 *
 * Adopted 2026-04-30 per the location-data-API audit. See CLAUDE.md
 * "Module Load Invariants" for why we never `await` 3rd-party APIs in
 * the route-load critical path.
 */

const PREFIX = 'aweni_swr:';

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

export interface SwrOptions<T> {
  /** How long the cached value is considered "fresh" — return without network. */
  freshTtlMs: number;
  /** Beyond freshTtlMs, return cached + revalidate. Default: 24h. */
  staleTtlMs?: number;
  /** Called when a background revalidation produces a new value. */
  onUpdate?: (data: T) => void;
  /**
   * If true, always issue the network call (no fresh-cache shortcut). Useful
   * for explicit "refresh" actions triggered by user.
   */
  force?: boolean;
}

function readCache<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (typeof parsed?.fetchedAt !== 'number') return null;
    return parsed;
  } catch { return null; }
}

function writeCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, fetchedAt: Date.now() }));
  } catch { /* quota exceeded — silently drop, swr just becomes a passthrough */ }
}

/**
 * Stale-while-revalidate fetch. Always returns fast (cached if available);
 * never throws (returns null on full failure with no fallback).
 */
export async function swr<T>(
  key: string,
  fetcher: () => Promise<T | null>,
  opts: SwrOptions<T>,
): Promise<T | null> {
  const staleTtlMs = opts.staleTtlMs ?? 24 * 60 * 60 * 1000;
  const cached = readCache<T>(key);
  const now = Date.now();
  const age = cached ? now - cached.fetchedAt : Infinity;

  // Fresh cache — return instantly, no network.
  if (!opts.force && cached && age < opts.freshTtlMs) {
    return cached.data;
  }

  // Stale cache — return cached + revalidate in background.
  if (!opts.force && cached && age < staleTtlMs) {
    void (async () => {
      try {
        const fresh = await fetcher();
        if (fresh !== null && fresh !== undefined) {
          writeCache(key, fresh);
          opts.onUpdate?.(fresh);
        }
      } catch { /* silent — caller already has stale data */ }
    })();
    return cached.data;
  }

  // No cache or beyond stale: do the fetch synchronously.
  try {
    const fresh = await fetcher();
    if (fresh !== null && fresh !== undefined) {
      writeCache(key, fresh);
      return fresh;
    }
  } catch { /* fall through to stale fallback */ }
  // Fetch failed — return stale if we have any (offline-tolerant).
  return cached?.data ?? null;
}

/** Remove a cached SWR entry — used by explicit "Clear cache" actions. */
export function swrInvalidate(key: string): void {
  try { localStorage.removeItem(PREFIX + key); } catch { /* */ }
}

/** Read the cached entry directly (no network). Useful for synchronous reads. */
export function swrPeek<T>(key: string): { data: T; fetchedAt: number } | null {
  return readCache<T>(key);
}
